import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getTrackingContext } from '../tracking/tracker';
import { useConsent } from '../tracking/consent';
import { affiliateApi } from './api';

/**
 * Zachycení partnerského odkazu `?ref=KÓD`.
 *
 * Nic nevykresluje. Když je v adrese `ref`, ohlásí serveru proklik a přiloží
 * anonymní klíč zařízení — TEN, a jenom ten, spojí pozdější objednávku
 * s partnerem. Klíč se bere z `getTrackingContext()`, takže BEZ SOUHLASU
 * S ANALYTIKOU se neposílá vůbec: affiliate cookie je marketingová a bez
 * souhlasu se k atribuci použít nesmí. Proklik se v takovém případě zapíše
 * jen do statistiky partnera; objednávka se pak dá přiřadit přes jeho kupón.
 *
 * SOUHLAS PŘICHÁZÍ POZDĚ. Návštěvník z partnerského odkazu uvidí cookie
 * lištu až na cílové stránce, takže první ohlášení klíč nemá. Jakmile
 * souhlas dá, ohlásí se proklik JEŠTĚ JEDNOU, už s klíčem — jinak by
 * partner o objednávku přišel jen kvůli pořadí kliknutí. Druhé ohlášení
 * je právě jedno, hlídá ho druhá známka v `sessionStorage`.
 *
 * JEDEN PROKLIK ZA NÁVŠTĚVU. Bez známek by každý návrat na stránku s `?ref`
 * (zpět v prohlížeči, reload) nafoukl statistiku partnera.
 *
 * FAIL-SILENT. Chyba se nikde neukazuje — návštěvník přišel nakoupit.
 */
const SEEN_KEY = 'zooshopik.affiliate.ref.v1';
const SEEN_KEYED = 'zooshopik.affiliate.refKeyed.v1';

function readMark(name: string): string | null {
  try {
    return window.sessionStorage.getItem(name);
  } catch {
    return null;
  }
}

function writeMark(name: string, value: string): void {
  try {
    window.sessionStorage.setItem(name, value);
  } catch {
    // Privátní režim – známka prostě nepřežije.
  }
}

export function RefCapture() {
  const location = useLocation();
  const { consent } = useConsent();

  useEffect(() => {
    const raw = new URLSearchParams(location.search).get('ref');
    if (raw === null || raw.trim() === '') return;
    const code = raw.trim().slice(0, 40);

    const tracking = getTrackingContext();
    const keyed = tracking !== undefined;

    // Anonymní ohlášení proběhne jednou; ohlášení S KLÍČEM taky jednou.
    const mark = keyed ? SEEN_KEYED : SEEN_KEY;
    if (readMark(mark) === code) return;
    writeMark(mark, code);
    // Klíčované ohlášení uzavírá i to anonymní – jinak by po odvolání
    // souhlasu přišlo ještě jedno navíc.
    if (keyed) writeMark(SEEN_KEY, code);

    void affiliateApi
      .click({
        code,
        ...(tracking === undefined
          ? {}
          : { visitorId: tracking.visitorId, sessionId: tracking.sessionId }),
        path: location.pathname,
      })
      .catch(() => {
        // Záměrně tiše.
      });
  }, [location.search, location.pathname, consent.analytics]);

  return null;
}
